import { useState } from "react";
import { __ } from "services/Translator";
import config from "configs/config";
import { useLimits } from "./useCashierData";
import InfoCard from "./InfoCard";
import { ShieldIcoIcon, ChatIcoIcon } from "./icons";

// Product-owner decision: the cashier shows ONLY the deposit limit. Loss,
// session and self-exclusion limits live in the player account (RG settings).
const RG_SETTINGS_PATH = "/dashboard/responsible-gaming";

const FIELD_STYLE = {
  width: "100%",
  padding: "11px 13px",
  borderRadius: "12px",
  background: "var(--glass-2)",
  border: "1px solid var(--stroke-soft)",
  color: "var(--ink)",
  fontFamily: "inherit",
  fontSize: "13px",
  colorScheme: "dark",
};

function formatDate(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function LimitsView({ cashier = {} }) {
  const { depositLimit, loading, saving, submitDepositLimit } = useLimits();
  const formatMoney = cashier.formatMoney || ((v) => String(v));

  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState("");
  const [feedback, setFeedback] = useState(null);

  const hasDeposit = !!(depositLimit && depositLimit.id);

  const openLiveChat = () => {
    if (config.liveChatUrl) window.open(config.liveChatUrl, "_blank", "noopener,noreferrer");
  };

  const apply = async () => {
    setFeedback(null);
    const numeric = parseFloat(amount);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setFeedback({ ok: false, message: __("Enter a deposit limit greater than zero.") });
      return;
    }
    if (!startDate || !endDate) {
      setFeedback({ ok: false, message: __("Choose a start and end date for the limit.") });
      return;
    }
    if (endDate < startDate) {
      setFeedback({ ok: false, message: __("The end date cannot be before the start date.") });
      return;
    }
    const res = await submitDepositLimit({ amount: numeric, startDate, endDate });
    setFeedback({
      ok: res.ok,
      message:
        res.message ||
        (res.ok ? __("Deposit limit saved.") : __("We could not save your deposit limit. Please try again.")),
    });
    if (res.ok) setAmount("");
  };

  return (
    <div className="stack">
      <InfoCard>
        <div className="ic-top">
          <span className="ic-ico em">
            <ShieldIcoIcon />
          </span>
          <div className="tt">
            <b>{__("Deposit limit")}</b>
            <small>
              {loading
                ? __("Loading...")
                : hasDeposit
                  ? __("Active") + (depositLimit.end_date ? " · " + __("until") + " " + formatDate(depositLimit.end_date) : "")
                  : __("No deposit limit set")}
            </small>
          </div>
          <span className="amt" style={{ color: "var(--em-300)" }}>
            {loading ? (
              <b className="skel" aria-hidden="true" />
            ) : hasDeposit ? (
              formatMoney(depositLimit.limit_amount)
            ) : (
              "—"
            )}
          </span>
        </div>

        {!loading && hasDeposit ? (
          <>
            <div className="prog-lbl">
              <span>
                {__("You can deposit up to")}{" "}
                <b style={{ color: "var(--em-300)" }}>{formatMoney(depositLimit.limit_amount)}</b>{" "}
                {depositLimit.start_date && depositLimit.end_date
                  ? __("between") + " " + formatDate(depositLimit.start_date) + " " + __("and") + " " + formatDate(depositLimit.end_date)
                  : ""}
              </span>
            </div>
            <div className="note-line" style={{ marginTop: "12px", fontSize: "11.5px" }}>
              {__("This limit is active and cannot be changed until it expires.")}
            </div>
          </>
        ) : null}
      </InfoCard>

      {!loading && !hasDeposit ? (
        <InfoCard>
          <div className="field-lbl" style={{ marginTop: 0 }}>
            {__("Set a deposit limit")}
          </div>
          <div className="note-line" style={{ fontSize: "11.5px", marginBottom: "14px" }}>
            {__(
              "Cap the total you can deposit between the dates you choose. Once it is active it cannot be changed until it expires."
            )}
          </div>
          <div style={{ display: "grid", gap: "10px" }}>
            <input
              type="number"
              inputMode="decimal"
              min="1"
              step="1"
              aria-label={__("Deposit limit amount")}
              placeholder={__("Amount")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={FIELD_STYLE}
            />
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <input
                type="date"
                aria-label={__("Start date")}
                min={today}
                max={endDate || undefined}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ ...FIELD_STYLE, flex: "1 1 120px", width: "auto" }}
              />
              <input
                type="date"
                aria-label={__("End date")}
                min={startDate || today}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ ...FIELD_STYLE, flex: "1 1 120px", width: "auto" }}
              />
            </div>
            <button className="miniclaim" onClick={apply} disabled={saving} style={{ marginLeft: 0 }}>
              {saving ? __("Saving...") : __("Apply limit")}
            </button>
          </div>
          {feedback ? (
            <div
              className="note-line"
              style={{ marginTop: "12px", fontSize: "12px", color: feedback.ok ? "var(--em-300)" : "#ff9c9c" }}
            >
              {feedback.message}
            </div>
          ) : null}
        </InfoCard>
      ) : null}

      <InfoCard>
        <div className="note-line">
          <b>{__("Loss, session and self-exclusion limits")}</b>{" "}
          {__("are managed in your account settings.")}
        </div>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          className="miniclaim ghost"
          href={RG_SETTINGS_PATH}
          style={{ display: "inline-block", marginTop: "12px", textDecoration: "none" }}
        >
          {__("Manage account limits")}
        </a>
      </InfoCard>

      {/* flexWrap + a real min basis on the copy: without them the row is a
          non-wrapping icon+text+button flex and the text column collapses to
          ~50px on 390px screens (renders as one word per line). The button
          drops to its own row when the copy needs the width. */}
      <InfoCard style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <span className="ic-ico em" style={{ width: "38px", height: "38px" }}>
          <ChatIcoIcon />
        </span>
        <div className="note-line" style={{ flex: "1 1 140px", minWidth: 0 }}>
          <b>{__("Need help?")}</b> {__("Our support team is available 24/7 via live chat.")}
        </div>
        <button className="miniclaim ghost" onClick={openLiveChat}>
          {__("Live chat")}
        </button>
      </InfoCard>
    </div>
  );
}
